import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  BulkImportValidationFailure,
  CompanyFeatures,
  ImportBrandResponse,
  ImportBrandsBulkResponse,
  ImportWarning,
  ImportValidationIssue,
  Role,
} from '@fametrue/shared';
import { ApiError, apiRequest } from '../lib/api';

export interface AdminCompany {
  id: string;
  name: string;
  slug: string;
  features: CompanyFeatures;
  createdAt: string;
}

export interface AdminBrand {
  id: string;
  slug: string;
  name: string;
  sector: string;
  sectorKey: string;
  companyId: string | null;
  webScore: string;
  fameIntegrity: number | null;
  matrixLabel: string;
  historyCount: number;
  importedAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  status: 'invited' | 'active' | 'disabled';
  companyId: string | null;
  /** Brand visibili al cliente; sempre vuoto per gli admin. */
  brandIds: string[];
  lastLoginAt: string | null;
}

export interface InviteResult {
  url: string;
  expiresAt: string;
}

export function useCompanies(): UseQueryResult<AdminCompany[]> {
  return useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: async () =>
      (await apiRequest<{ companies: AdminCompany[] }>('/api/admin/companies')).companies,
  });
}

export function useAdminBrands(): UseQueryResult<AdminBrand[]> {
  return useQuery({
    queryKey: ['admin', 'brands'],
    queryFn: async () => (await apiRequest<{ brands: AdminBrand[] }>('/api/admin/brands')).brands,
  });
}

export function useAdminUsers(): UseQueryResult<AdminUser[]> {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => (await apiRequest<{ users: AdminUser[] }>('/api/admin/users')).users,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiRequest<{ company: AdminCompany }>('/api/admin/companies', {
        method: 'POST',
        body: { name },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] }),
  });
}

export function useUpdateFeatures() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; features: CompanyFeatures }) =>
      apiRequest<{ company: AdminCompany }>(`/api/admin/companies/${input.companyId}/features`, {
        method: 'PATCH',
        body: input.features,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] }),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      firstName: string;
      lastName: string;
      role: Role;
      companyId?: string;
      brandIds?: string[];
    }) =>
      apiRequest<{ user: AdminUser; invite: InviteResult }>('/api/admin/users', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

/** Sostituisce per intero l'elenco dei brand visibili all'utente. */
export function useUpdateUserBrands() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; brandIds: string[] }) =>
      apiRequest<{ user: { id: string; brandIds: string[] } }>(
        `/api/admin/users/${input.userId}/brands`,
        { method: 'PUT', body: { brandIds: input.brandIds } },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

/**
 * Disattiva o riattiva un utente. La disattivazione ha effetto immediato: il
 * server chiude le sessioni aperte e annulla l'invito ancora in sospeso.
 */
export function useUpdateUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; status: 'active' | 'disabled' }) =>
      apiRequest<{ user: { id: string; status: AdminUser['status'] } }>(
        `/api/admin/users/${input.userId}/status`,
        { method: 'PATCH', body: { status: input.status } },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useReissueInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiRequest<{ invite: InviteResult }>(`/api/admin/users/${userId}/invite`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

/**
 * Elimina l'utente. Le sue sessioni, gli inviti e l'elenco dei brand visibili
 * cadono con lui a database; i brand che ha importato restano.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiRequest<void>(`/api/admin/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useAssignBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { brandId: string; companyId: string | null }) =>
      apiRequest(`/api/admin/brands/${input.brandId}/company`, {
        method: 'PATCH',
        body: { companyId: input.companyId },
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'brands'] }),
        // Il cambio di company pota gli elenchi degli utenti della vecchia.
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
      ]),
  });
}

export function useDeleteBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (brandId: string) =>
      apiRequest<void>(`/api/admin/brands/${brandId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'brands'] });
      void queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
  });
}

/**
 * Esito dell'import così come serve all'interfaccia: l'API distingue tre casi
 * e vanno tenuti separati, perché richiedono azioni diverse dall'admin.
 */
export type ImportOutcome =
  | { kind: 'ok'; result: ImportBrandResponse }
  | { kind: 'okBulk'; result: ImportBrandsBulkResponse }
  /** 422: il JSON viola lo schema, va corretto a monte. */
  | { kind: 'invalid'; issues: ImportValidationIssue[] }
  /** 422 del caricamento in blocco: errori raggruppati per brand. */
  | { kind: 'invalidBulk'; failures: BulkImportValidationFailure[] }
  /** 409: avvisi non bloccanti, si procede solo con conferma esplicita. */
  | { kind: 'needsConfirmation'; warnings: ImportWarning[] }
  | { kind: 'error'; error: unknown };

function outcomeFromError(error: unknown): ImportOutcome {
  if (error instanceof ApiError && error.code === 'brand_validation_failed') {
    return { kind: 'invalid', issues: (error.details ?? []) as ImportValidationIssue[] };
  }
  if (error instanceof ApiError && error.code === 'bulk_validation_failed') {
    return {
      kind: 'invalidBulk',
      failures: (error.details ?? []) as BulkImportValidationFailure[],
    };
  }
  if (error instanceof ApiError && error.code === 'import_needs_confirmation') {
    return { kind: 'needsConfirmation', warnings: (error.details ?? []) as ImportWarning[] };
  }
  return { kind: 'error', error };
}

function useInvalidateBrands(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'brands'] });
    void queryClient.invalidateQueries({ queryKey: ['brands'] });
  };
}

export function useImportBrand() {
  const invalidate = useInvalidateBrands();

  return useMutation<
    ImportOutcome,
    never,
    { brand: unknown; companyId?: string | undefined; acknowledgeWarnings?: boolean }
  >({
    mutationFn: async (input): Promise<ImportOutcome> => {
      try {
        const result = await apiRequest<ImportBrandResponse>('/api/admin/brands/import', {
          method: 'POST',
          body: {
            brand: input.brand,
            ...(input.companyId ? { companyId: input.companyId } : {}),
            ...(input.acknowledgeWarnings ? { acknowledgeWarnings: true } : {}),
          },
        });
        return { kind: 'ok', result };
      } catch (error) {
        return outcomeFromError(error);
      }
    },
    onSuccess: (outcome) => {
      if (outcome.kind === 'ok') invalidate();
    },
  });
}

/**
 * Caricamento in blocco di tutti i brand del file. Nessuna company: il server
 * risponde sempre con un 409 al primo invio, che il pannello mostra come avviso
 * da confermare, nello stesso flusso degli altri avvisi.
 */
export function useImportBrandsBulk() {
  const invalidate = useInvalidateBrands();

  return useMutation<ImportOutcome, never, { brands: unknown[]; acknowledgeWarnings?: boolean }>({
    mutationFn: async (input): Promise<ImportOutcome> => {
      try {
        const result = await apiRequest<ImportBrandsBulkResponse>('/api/admin/brands/import-all', {
          method: 'POST',
          body: {
            brands: input.brands,
            ...(input.acknowledgeWarnings ? { acknowledgeWarnings: true } : {}),
          },
        });
        return { kind: 'okBulk', result };
      } catch (error) {
        return outcomeFromError(error);
      }
    },
    onSuccess: (outcome) => {
      if (outcome.kind === 'okBulk') invalidate();
    },
  });
}
